/**
 * Google Analytics Event Tracking Utility
 * 提供全局的事件跟踪功能，封装 gtag 方法为更易用的接口
 * 
 * 使用示例:
 * 1. 组件内使用: this.$gtag.event('button_click', {event_category: 'engagement', event_label: 'homepage'})
 * 2. 工具函数使用: import { trackEvent } from '@/utils/gtagHelper' 
 *                 trackEvent('button_click', {event_category: 'engagement', event_label: 'homepage'})
 */

// 缓存用户IP地址
let cachedIP = null

// 获取用户IP地址的函数
const getUserIP = () => {
  // 如果已有缓存IP，直接返回
  if (cachedIP) {
    return cachedIP
  }

  try {
    // 1. 尝试从localStorage获取保存的IP
    const storedIP = localStorage.getItem('user_ip')
    if (storedIP) {
      cachedIP = storedIP
      return storedIP
    }

    // 2. 尝试从window对象获取（如果由其他脚本设置）
    if (window.userIP) {
      cachedIP = window.userIP
      localStorage.setItem('user_ip', window.userIP)
      return window.userIP
    }

    // 3. 尝试通过API获取IP（这里可以根据实际情况替换为项目中的IP获取接口）
    // 注意：这里不进行实际的异步请求，因为会阻塞跟踪函数
    // 实际项目中可以在应用初始化时异步获取IP并设置到window对象
    
    // 4. 回退方案：使用空字符串或未知标记
    return 'unknown'
  } catch (error) {
    // console.warn('Error getting user IP:', error)
    return 'unknown'
  }
}

// 检查 gtag 是否已加载
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.dataLayer !== 'undefined' && typeof window.gtag !== 'undefined'
}

/**
 * 跟踪事件的核心函数
 * @param {string} action - 事件动作名称
 * @param {object} params - 事件参数对象，可包含custom_ip覆盖自动获取的IP
 * @returns {boolean} - 是否成功跟踪事件
 */
export const trackEvent = (action, params = {}) => {
  // 检查gtag是否已加载，如果没有则不执行跟踪操作
  if (!isGtagAvailable()) {
    console.warn('Google Analytics is not loaded yet. Event tracking skipped.')
    return false
  }

  try {
    // 获取IP（使用传入的custom_ip或自动获取）
    const userIP = params.custom_ip || getUserIP()
    
    // 确保必需的参数存在，并添加IP信息
    const eventParams = {
      event_category: params.event_category || 'default',
      event_label: params.event_label || '',
      value: params.value || undefined,
      // 添加IP信息作为自定义维度
      custom_dimensions: {
        ...(params.custom_dimensions || {}),
        user_ip: userIP
      },
      // 直接添加IP作为自定义参数
      user_ip: userIP,
      // 添加可选的自定义维度和指标
      ...params
    }
    
    window.gtag('event', action, eventParams)
    return true
  } catch (error) {
    console.error('Error tracking Google Analytics event:', error)
    return false
  }
}

/**
 * 跟踪页面浏览
 * @param {string} pagePath - 页面路径
 * @param {string} pageTitle - 页面标题
 * @param {object} params - 额外参数，可包含custom_ip覆盖自动获取的IP
 * @returns {boolean} - 是否成功跟踪页面浏览
 */
export const trackPageView = (pagePath = window.location.pathname + window.location.search, pageTitle = document.title, params = {}) => {
  if (!isGtagAvailable()) {
    console.warn('Google Analytics is not loaded yet. Page view tracking skipped.')
    return false
  }

  try {
    // 获取IP（使用传入的custom_ip或自动获取）
    const userIP = params.custom_ip || getUserIP()
    
    // 合并配置参数，添加IP信息
    const configParams = {
      page_path: pagePath,
      page_title: pageTitle,
      // 添加IP信息作为自定义维度
      custom_dimensions: {
        ...(params.custom_dimensions || {}),
        user_ip: userIP
      },
      // 直接添加IP作为自定义参数
      user_ip: userIP,
      // 添加其他自定义参数
      ...params
    }
    
    window.gtag('config', 'G-K2WL24WV58', configParams)
    return true
  } catch (error) {
    console.error('Error tracking Google Analytics page view:', error)
    return false
  }
}

/**
 * 跟踪用户时间
 * @param {string} category - 时间类别
 * @param {string} action - 时间动作
 * @param {string} label - 时间标签
 * @param {number} value - 持续时间（秒）
 * @param {object} params - 额外参数，可包含custom_ip覆盖自动获取的IP
 * @returns {boolean} - 是否成功跟踪时间
 */
export const trackTiming = (category, action, label, value, params = {}) => {
  if (!isGtagAvailable()) {
    console.warn('Google Analytics is not loaded yet. Timing tracking skipped.')
    return false
  }

  try {
    // 获取IP（使用传入的custom_ip或自动获取）
    const userIP = params.custom_ip || getUserIP()
    
    // 构建时间跟踪参数，添加IP信息
    const timingParams = {
      name: action,
      value: value,
      event_category: category,
      event_label: label,
      // 添加IP信息作为自定义维度
      custom_dimensions: {
        ...(params.custom_dimensions || {}),
        user_ip: userIP
      },
      // 直接添加IP作为自定义参数
      user_ip: userIP,
      // 添加其他自定义参数
      ...params
    }
    
    window.gtag('event', 'timing_complete', timingParams)
    console.log('GA Timing tracked:', category, action, label, value)
    return true
  } catch (error) {
    console.error('Error tracking Google Analytics timing:', error)
    return false
  }
}

/**
 * 跟踪异常
 * @param {string} description - 异常描述
 * @param {boolean} fatal - 是否致命错误
 * @param {object} params - 额外参数，可包含custom_ip覆盖自动获取的IP
 * @returns {boolean} - 是否成功跟踪异常
 */
export const trackException = (description, fatal = false, params = {}) => {
  if (!isGtagAvailable()) {
    console.warn('Google Analytics is not loaded yet. Exception tracking skipped.')
    return false
  }

  try {
    // 获取IP（使用传入的custom_ip或自动获取）
    const userIP = params.custom_ip || getUserIP()
    
    // 构建异常跟踪参数，添加IP信息
    const exceptionParams = {
      description: description,
      fatal: fatal,
      // 添加IP信息作为自定义维度
      custom_dimensions: {
        ...(params.custom_dimensions || {}),
        user_ip: userIP
      },
      // 直接添加IP作为自定义参数
      user_ip: userIP,
      // 添加其他自定义参数
      ...params
    }
    
    window.gtag('event', 'exception', exceptionParams)
    console.log('GA Exception tracked:', description, 'fatal:', fatal)
    return true
  } catch (error) {
    console.error('Error tracking Google Analytics exception:', error)
    return false
  }
}

/**
 * 设置用户IP（可以从外部调用此方法设置IP）
 * @param {string} ip - 用户IP地址
 */
export const setUserIP = (ip) => {
  if (ip && typeof ip === 'string') {
    cachedIP = ip
    try {
      localStorage.setItem('user_ip', ip)
      window.userIP = ip
    } catch (error) {
      console.warn('Error caching user IP:', error)
    }
  }
}

/**
 * Vue 插件安装函数
 * @param {Vue} Vue - Vue 构造函数
 * @param {Object} options - 插件配置选项
 */
export const install = (Vue, options = {}) => {
  // 如果选项中提供了初始IP，设置它
  if (options.initialIP) {
    setUserIP(options.initialIP)
  }
  
  // 挂载到 Vue 原型上，使组件实例可以通过 this.$gtag 访问
  Vue.prototype.$gtag = {
    event: trackEvent,
    pageView: trackPageView,
    timing: trackTiming,
    exception: trackException,
    setUserIP: setUserIP
  }
  
  // 挂载到全局 Vue 对象上（静态方法），可以通过 Vue.gtag 访问
  Vue.gtag = {
    event: trackEvent,
    pageView: trackPageView,
    timing: trackTiming,
    exception: trackException,
    setUserIP: setUserIP
  }
}

// 默认导出
const GtagPlugin = {
  install,
  trackEvent,
  trackPageView,
  trackTiming,
  trackException,
  setUserIP
}

export default GtagPlugin